import { THEME_COLOR_NAMES, type ThemeColorName } from '@examdojo/styles';
import type { ElementLabelConfig, LabelPosition, LabelTextAlign, LabelVariant } from '../label/label.model';
import type { SnappingConfig } from '../point/snapping.model';

export const THEME_COLOR_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type ThemeColorShade = (typeof THEME_COLOR_SHADES)[number];

export const NEUTRAL_EDGE_SHADES = [0, 1000] as const;
export type NeutralEdgeShade = (typeof NEUTRAL_EDGE_SHADES)[number];

export type ThemeColorWithShade =
  | ThemeColorName
  | `${ThemeColorName}-${ThemeColorShade}`
  | `neutral-${NeutralEdgeShade}`;

export const THEME_COLORS_WITH_SHADES: readonly ThemeColorWithShade[] = [
  ...NEUTRAL_EDGE_SHADES.map((s): ThemeColorWithShade => `neutral-${s}`),
  ...THEME_COLOR_NAMES,
  ...THEME_COLOR_NAMES.flatMap((name) => THEME_COLOR_SHADES.map((shade): ThemeColorWithShade => `${name}-${shade}`)),
];

/**
 * Point face types supported by JSXGraph.
 */
export const POINT_FACES = ['o', 'x', '+', '<>', '[]', '^', 'v', '<', '>', 'triangledown'] as const;
export type PointFace = (typeof POINT_FACES)[number];

// -----------------------------------------------------------------------------
// POINT STYLE
// -----------------------------------------------------------------------------
export interface PointStyle {
  /** Whether the point is visible */
  visible?: boolean;
  /** Whether the point is fixed (non-draggable) */
  fixed?: boolean;
  /** Size of the point in pixels */
  size?: number;
  /** Shape of the point */
  face?: PointFace;
  /** Point color (fill color - the main visual color of the point) */
  color?: ThemeColorWithShade;
  /** Optional stroke color (outline). If not set, no stroke is shown. */
  strokeColor?: ThemeColorWithShade;
  /** Fill opacity (0-1) */
  fillOpacity?: number;
  /** Stroke width in pixels (only applies if strokeColor is set) */
  strokeWidth?: number;
  /** Label configuration (true for default, false for none, or config object) */
  label?: ElementLabelConfig | boolean;
  /** Snapping configuration (grid and/or attractor snapping) */
  snapping?: SnappingConfig;
  /** Whether to show a teardrop grab handle above the point for touch-friendly dragging */
  showGrabHandle?: boolean;
}

// -----------------------------------------------------------------------------
// LINE CONFIG
// -----------------------------------------------------------------------------
/** Arrow style configuration for individual arrow heads */
export interface ArrowStyleConfig {
  /** Arrow type (1-7 for different JSXGraph arrow styles) */
  type?: number;
  /** Arrow size in pixels */
  size?: number;
}

export interface ArrowConfig {
  first?: boolean | ArrowStyleConfig;
  last?: boolean | ArrowStyleConfig;
  /** Default arrow type when first/last is set to true (default: 2) */
  type?: number;
  /** Default arrow size when first/last is set to true (default: 8) */
  size?: number;
}

export type DashPattern = number; //0 = solid, 1 = dotted, 2 = dashed, etc.

export interface LineStyle {
  /** Stroke color */
  color?: ThemeColorWithShade;
  /** Stroke width in pixels */
  strokeWidth?: number;
  /** Dash pattern */
  dash?: DashPattern;
  /** Arrow configuration */
  arrows?: ArrowConfig;
  /** Whether the line is visible */
  visible?: boolean;
  /** Label configuration (true for default, false for none, or config object) */
  label?: ElementLabelConfig | boolean;
  /** Extend line infinitely before first point (default: false = segment behavior) */
  straightFirst?: boolean;
  /** Extend line infinitely after last point (default: false = segment behavior) */
  straightLast?: boolean;
}

// -----------------------------------------------------------------------------
// POLYGON STYLE
// -----------------------------------------------------------------------------
export interface PolygonStyle {
  /** Fill color */
  fillColor?: ThemeColorWithShade;
  /** Fill opacity (0-1) */
  fillOpacity?: number;
  /** Stroke color */
  strokeColor?: ThemeColorWithShade;
  /** Stroke width in pixels */
  strokeWidth?: number;
  /** Whether the polygon is visible */
  visible?: boolean;
  /** Label configuration (true for default, false for none, or config object) */
  label?: ElementLabelConfig | boolean;
}

// -----------------------------------------------------------------------------
// CURVE STYLE
// -----------------------------------------------------------------------------
export interface CurveStyle {
  /** Stroke color */
  color?: ThemeColorWithShade;
  /** Stroke width in pixels */
  strokeWidth?: number;
  /** Dash pattern */
  dash?: DashPattern;
  /** Arrow configuration */
  arrows?: ArrowConfig;
  /** Whether the curve is visible */
  visible?: boolean;
  /** Label configuration (true for default, false for none, or config object) */
  label?: ElementLabelConfig | boolean;
}

// -----------------------------------------------------------------------------
// ANGLE STYLE
// -----------------------------------------------------------------------------
export type AngleType = 'sector' | 'square' | 'none';

export interface AngleStyle {
  /** Fill color */
  fillColor?: ThemeColorWithShade;
  /** Fill opacity (0-1) */
  fillOpacity?: number;
  /** Stroke color */
  strokeColor?: ThemeColorWithShade;
  /** Radius of the angle arc */
  radius?: number;
  /** Type of angle display */
  type?: AngleType;
  /** Whether the angle is visible */
  visible?: boolean;
  /** Label configuration (true for default, false for none, or config object) */
  label?: ElementLabelConfig | boolean;
}

// -----------------------------------------------------------------------------
// CIRCLE STYLE
// -----------------------------------------------------------------------------
export interface CircleStyle {
  /** Stroke color */
  strokeColor?: ThemeColorWithShade;
  /** Fill color */
  fillColor?: ThemeColorWithShade;
  /** Fill opacity (0-1) */
  fillOpacity?: number;
  /** Stroke width in pixels */
  strokeWidth?: number;
  /** Dash pattern */
  dash?: DashPattern;
  /** Whether the circle is visible */
  visible?: boolean;
  /** Label configuration (true for default, false for none, or config object) */
  label?: ElementLabelConfig | boolean;
}

// -----------------------------------------------------------------------------
// LABEL DISPLAY STYLE (for standalone label elements)
// -----------------------------------------------------------------------------
export interface LabelDisplayStyle {
  /** Label variant: primary (filled bg), secondary (light bg), tertiary (no bg) */
  variant?: LabelVariant;
  /** Theme color for the label */
  color?: ThemeColorName;
  /** Whether the label is visible */
  visible?: boolean;
  /** Position relative to the anchor element (top, bottom, left, right, etc.) */
  position?: LabelPosition;
  /** Pixel offset from the computed position [dx, dy] */
  offset?: [number, number];
  /** When true, the label is hidden by default and shown on hover over the anchor/target element */
  popup?: boolean;
  /** Font size in pixels. Overrides the theme default. */
  fontSize?: number;
  /** Text alignment for multi-line labels. */
  textAlign?: LabelTextAlign;
}

// -----------------------------------------------------------------------------
// ELEMENT THEME
// -----------------------------------------------------------------------------
export interface ElementTheme {
  /** Default point styles (strokeColor, label, snapping, showGrabHandle are optional) */
  point: Required<Omit<PointStyle, 'fixed' | 'strokeColor' | 'label' | 'snapping' | 'showGrabHandle'>>;
  /** Default line styles with vertex defaults */
  line: Required<Omit<LineStyle, 'arrows' | 'label'>> & {
    vertex: PointStyle;
  };
  /** Default polygon styles with vertex and border defaults */
  polygon: Required<Omit<PolygonStyle, 'visible' | 'label'>> & {
    vertex: PointStyle;
    border: LineStyle;
  };
  /** Default curve styles */
  curve: Required<Omit<CurveStyle, 'visible' | 'label'>>;
  /** Default angle styles */
  angle: Required<Omit<AngleStyle, 'visible' | 'label'>>;
  /** Default circle styles with center point defaults */
  circle: Required<Omit<CircleStyle, 'visible' | 'label'>> & {
    center: PointStyle;
  };
}
